<template>
  <div>
    <data-table 
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="tableEndpoint"
      :filterConfig="filterConfig"
      :adjustColumnsWidth="adjustColumnsWidthConfig"
      :refreshButton="refreshButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data?: Record<string, unknown>;
  value?: unknown;
}

const { t } = useI18n();

const props = withDefaults(defineProps<{
  compromissoId?: number;
}>(), {
  compromissoId: 0
});

const emit = defineEmits<{
  'edit-compromisso-servico': [compromissoServicoId: Id];
}>();

const dataTableRef = ref<DataTableInstance | null>(null);

const tableEndpoint = computed<string | undefined>(() => {
  if (!props.compromissoId || props.compromissoId === 0) {
    return undefined;
  }
  return `/index.php/compromissos_servicos/listByCompromissoID/${props.compromissoId}`;
});

const tableColumns = computed(() => {
  return [
    {
      data: 'compromisso_servico_id',
      title: t('ID'),
      width: 100,
      renderType: 'clickable_id'
    },
    {
      data: 'servico_label',
      title: t('Serviço')
    },
    {
      data: 'compromisso_servico_valor',
      title: t('Valor'),
      width: 120,
      renderType: 'currency',
      footerType: 'sum',
      footerCurrency: true
    },
    {
      data: 'compromisso_servico_qtd',
      title: t('Quantidade')
    },
    {
      data: 'orcamento_servico_tipo',
      title: t('Tipo'),
      width: 180,
      cellRenderer: (params: CellParams) => {
        if (params.value == 1) {
          return t('Agendado no orçamento');
        } else {
          return t('Agendado no compromisso');
        }
      }
    },
    {
      data: 'compromisso_servico_status',
      title: t('Status'),
      width: 150,
      cellRenderer: (params: CellParams) => {
        // 3 Serviço agendado, 4 Em andamento, 5 Serviço realizado, 6 Serviço cancelado
        let statusText: unknown = params.value;
        let severity = 'info';
        let icon = 'question';

        if (params.value == 3) {
          statusText = t('Serviço agendado');
          severity = 'warning';
          icon = 'clock';
        } else if (params.value == 4) {
          statusText = t('Em andamento');
          severity = 'info';
          icon = 'spin';
        } else if (params.value == 5) {
          statusText = t('Serviço realizado');
          severity = 'success';
          icon = 'check';
        } else if (params.value == 6) {
          statusText = t('Serviço cancelado');
          severity = 'danger';
          icon = 'times';
        }

        const span = document.createElement('span');
        span.className = `status-badge status-${severity}`;
        span.innerHTML = `<i class='pi pi-${icon}'></i> ${statusText}`;
        return span;
      }
    }
  ];
});

const tableConfig = computed(() => {
  return {
    tableId: 'tabelaCompromissosServico',
    columns: tableColumns.value,
    pageLength: 25,
    height: '400px',
    pagination: true,
    rowSelection: 'single'
  };
});

const filterConfig = computed(() => {
  const config = {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'compromisso_servico_id': 'text',
      'servico_label': 'text',
      'compromisso_servico_valor': 'text',
      'compromisso_servico_qtd': 'text',
      'orcamento_servico_tipo': 'text',
      'compromisso_servico_status': 'text'
    }
  };
  log('CompromissoServicoTable', 'filterConfig', 'Config built');
  return config;
});

const adjustColumnsWidthConfig = computed(() => {
  return {
    show: false // Disabled for now
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const loadData = (): void => {
  log('CompromissoServicoTable', 'loadData', 'Triggering grid refresh');
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('CompromissoServicoTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t(`Carregados`) + ' ' + data.length + ' ' + t(`registros de serviços do compromisso`));
};

const handleDataError = (error: unknown): void => {
  log('CompromissoServicoTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : String(error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar serviços do compromisso') + ': ' + message);
};

const editItem = (compromissoServicoId: Id): void => {
  log('CompromissoServicoTable', 'editItem', 'Editing ID', compromissoServicoId);
  emit('edit-compromisso-servico', compromissoServicoId);
};

const handleRowClick = (rowData?: Record<string, unknown>): void => {
  log('CompromissoServicoTable', 'handleRowClick', 'Row clicked', rowData?.compromisso_servico_id || null);
};

const handleEditClick = (compromissoServicoId: Id): void => {
  log('CompromissoServicoTable', 'handleEditClick', 'Edit clicked for ID', compromissoServicoId);
  editItem(compromissoServicoId);
};

const handleServicoSaved = (): void => {
  log('CompromissoServicoTable', 'event:compromisso.servico.saved', 'Received');
  loadData();
};

onMounted(() => {
  log('CompromissoServicoTable', 'mounted', 'Setting up event listeners');
  eventBus.on('compromisso.servico.saved', handleServicoSaved);
});

onBeforeUnmount(() => {
  log('CompromissoServicoTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('compromisso.servico.saved', handleServicoSaved);
});

// Expose public methods for parent components
defineExpose({
  loadData,
  editItem
});
</script>

<style scoped>
.text-center {
  text-align: center;
}

:deep(.status-badge) {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

:deep(.status-badge i) {
  font-size: 0.75rem;
}

:deep(.status-warning) {
  background-color: #fff3cd;
  color: #856404;
}

:deep(.status-info) {
  background-color: #d1ecf1;
  color: #0c5460;
}

:deep(.status-success) {
  background-color: #d4edda;
  color: #155724;
}

:deep(.status-danger) {
  background-color: #f8d7da;
  color: #721c24;
}

:deep(.pi-spin) {
  animation: pi-spin 2s infinite linear;
}

@keyframes pi-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>
